import { useCallback, useEffect, useState } from "react";
import { Send, FlaskConical, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LetterStatusTable } from "@/components/LetterStatusTable";

function SendLog({ items }) {
  if (!items.length) return <p className="text-xs text-slate-400" data-testid="letters-log-empty">Nessun invio registrato</p>;
  return (
    <div className="max-h-48 overflow-y-auto border border-slate-100 rounded-md" data-testid="letters-log">
      <table className="w-full text-xs">
        <tbody>
          {items.map((x) => (
            <tr key={x.id} className="border-b border-slate-50">
              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDateTime(x.at)}</td>
              <td className="px-2 py-1.5">{x.name}<span className="block text-slate-400">{x.email}</span></td>
              <td className="px-2 py-1.5">{x.kind}</td>
              <td className={`px-2 py-1.5 font-medium ${x.status === "inviata" ? "text-emerald-700" : "text-red-700"}`} title={x.error || ""}>{x.status}</td>
              <td className="px-2 py-1.5 text-slate-400">{x.operator.split("@")[0]}</td>
              <td className="px-2 py-1.5">{x.kind === "prova" ? "" : x.first_login_at ? <span className="text-emerald-700">entrato</span> : <span className="text-amber-700">non ancora</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LetterEmailSection() {
  const [pv, setPv] = useState(null);
  const [log, setLog] = useState([]);
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/letters/access/email/preview").then((r) => setPv(r.data)).catch(() => {});
    api.get("/letters/access/email/log").then((r) => setLog(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!pv?.running) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [pv?.running, load]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); load(); } };
  const test = () => run(async () => { const { data } = await api.post("/letters/access/email/test"); toast.success(`Email di prova inviata a ${data.sent_to}`); });
  const n = pv ? (resend ? pv.recipients.length : pv.to_send) : 0;
  const sendAll = () => run(async () => {
    if (!window.confirm(`Inviare la lettera via email a ${n} clienti? L'operazione verrà registrata.`)) return;
    const { data } = await api.post("/letters/access/email/send", { resend });
    toast.success(`Invio avviato: ${data.total} email`);
  });

  if (!pv) return null;
  return (
    <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="letters-email-section">
      <p className="font-heading font-semibold text-sm text-slate-900">Invio via email</p>
      <p className="text-xs text-slate-500" data-testid="letters-email-summary">
        {pv.recipients.length} clienti con utenza attiva · {pv.to_send} da inviare · {pv.already_sent} già raggiunti
        {pv.excluded_without_user > 0 ? ` · ${pv.excluded_without_user} anagrafiche senza utenza escluse (lettera solo cartacea)` : ""}
      </p>
      {pv.running && <p className="text-xs text-sky-700 flex items-center gap-1" data-testid="letters-email-running"><RefreshCw className="h-3 w-3 animate-spin" />Invio in corso ({pv.running.total} email)…</p>}
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} data-testid="letters-email-resend" />
        Reinvia anche a chi l'ha già ricevuta
      </label>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={test} data-testid="letters-email-test"><FlaskConical className="h-4 w-4 mr-1" />Invio di prova a me</Button>
        <Button size="sm" disabled={busy || !!pv.running || n === 0} onClick={sendAll} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="letters-email-send-all">
          <Send className="h-4 w-4 mr-1" />Invia a tutti ({n})
        </Button>
        <Button variant="ghost" size="sm" onClick={() => downloadFile("/letters/access/email/log.csv", "registro_invii_lettere.csv")} data-testid="letters-log-csv"><Download className="h-4 w-4 mr-1" />Registro CSV</Button>
      </div>
      <LetterStatusTable running={pv.running} onChanged={load} />
      <p className="font-heading font-semibold text-sm text-slate-900 pt-2">Registro invii</p>
      <SendLog items={log} />
    </div>
  );
}
